import { format as formatDateFns } from 'date-fns';
import type { ItpDataGridColumn, GridFormatter } from './types';
import {
  csvNumber,
  csvDelimiterFor,
  DEFAULT_LOCALE,
  DEFAULT_DATE_FORMAT,
} from './gridI18n';
import { columnValueLookup } from './gridCell';
import { gridMessages } from '../../messages';

export type ColumnFormatterLookup = (
  column: ItpDataGridColumn,
) => GridFormatter | null;

export interface ExportLocaleOptions {
  locale?: string;
  dateFormat?: string;
}

export interface CsvExportOptions extends ExportLocaleOptions {
  filename?: string;
  delimiter?: string;
  formatterFor?: ColumnFormatterLookup;
}

export interface XlsxExportOptions {
  filename?: string;
  sheet?: string;
  dateFormat?: string;
  formatterFor?: ColumnFormatterLookup;
}

// ── Shared: export-value precedence (exportRawValue → toValue → toText → type) ──
function formattedExportValue(
  value: any,
  row: any,
  column: ItpDataGridColumn,
  formatterFor?: ColumnFormatterLookup,
): string | number | Date | null | undefined {
  if (column.exportRawValue || !formatterFor) {
    return undefined;
  }
  const formatter = formatterFor(column);
  if (!formatter) {
    return undefined;
  }
  if (formatter.toValue) {
    return formatter.toValue(value, row);
  }
  if (formatter.toText) {
    return formatter.toText(value, row);
  }
  return undefined;
}

export function exportableColumns(
  columns: ItpDataGridColumn[],
): ItpDataGridColumn[] {
  return columns.filter(
    (column) => column.exportable !== false && !column.hidden,
  );
}

function cellText(value: any): string {
  if (value == null) {
    return '';
  }
  return Array.isArray(value) ? value.join(', ') : String(value);
}

// ── CSV ──
function csvCellText(
  row: any,
  column: ItpDataGridColumn,
  value: any,
  options: Required<ExportLocaleOptions>,
  formatterFor?: ColumnFormatterLookup,
): string {
  const provided = formattedExportValue(value, row, column, formatterFor);
  if (provided !== undefined) {
    if (typeof provided === 'number' && Number.isFinite(provided)) {
      return csvNumber(provided, options.locale);
    }
    if (provided instanceof Date && !Number.isNaN(provided.getTime())) {
      return formatDateFns(provided, options.dateFormat);
    }
    return provided == null ? '' : String(provided);
  }

  if (value == null) {
    return '';
  }

  if (column.type === 'number') {
    const asNumber = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(asNumber)) {
      return csvNumber(asNumber, options.locale);
    }
  }

  if (column.type === 'date') {
    const asDate = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(asDate.getTime())) {
      return formatDateFns(asDate, options.dateFormat);
    }
  }

  return cellText(value);
}

function escapeCsv(text: string, delimiter: string): string {
  if (
    text.includes(delimiter) ||
    text.includes('"') ||
    text.includes('\n') ||
    text.includes('\r')
  ) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function toCsv(
  rows: readonly any[],
  columns: ItpDataGridColumn[],
  options: CsvExportOptions = {},
): string {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const delimiter = options.delimiter ?? csvDelimiterFor(locale);
  const localeOptions = {
    locale,
    dateFormat: options.dateFormat ?? DEFAULT_DATE_FORMAT,
  };

  const cols = exportableColumns(columns);
  const valueFor = columnValueLookup(cols);
  const header = cols
    .map((column) => escapeCsv(column.name || column.field, delimiter))
    .join(delimiter);
  const lines = rows.map((row) =>
    cols
      .map((column) =>
        escapeCsv(
          csvCellText(
            row,
            column,
            valueFor(row, column),
            localeOptions,
            options.formatterFor,
          ),
          delimiter,
        ),
      )
      .join(delimiter),
  );
  return [header, ...lines].join('\r\n');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function downloadCsv(
  rows: readonly any[],
  columns: ItpDataGridColumn[],
  options: CsvExportOptions = {},
): void {
  const BOM = '﻿';
  const content = BOM + toCsv(rows, columns, options);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${options.filename ?? 'export'}.csv`);
}

// ── Excel (.xlsx) ──
export interface XlsxCell {
  value: string | number | Date | null;
  type?: StringConstructor | NumberConstructor | DateConstructor;
  format?: string;
  fontWeight?: 'bold';
  align?: 'left' | 'center' | 'right';
}

const NUMBER_FORMAT = '#,##0.00';
const FALLBACK_EXCEL_DATE_FORMAT = 'yyyy-mm-dd';

function excelWidth(pixels: number | undefined): number | undefined {
  if (!pixels) {
    return undefined;
  }
  return Math.min(60, Math.max(8, Math.round(pixels / 7)));
}

function xlsxCell(
  row: any,
  column: ItpDataGridColumn,
  value: any,
  dateFormat: string,
  formatterFor?: ColumnFormatterLookup,
): XlsxCell {
  const provided = formattedExportValue(value, row, column, formatterFor);
  if (provided !== undefined) {
    if (typeof provided === 'number' && Number.isFinite(provided)) {
      return { value: provided, type: Number, format: NUMBER_FORMAT };
    }
    if (provided instanceof Date && !Number.isNaN(provided.getTime())) {
      return { value: provided, type: Date, format: dateFormat };
    }
    return provided == null
      ? { value: null }
      : { value: String(provided), type: String };
  }

  if (value == null) {
    return { value: null };
  }

  if (column.type === 'number') {
    const asNumber = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(asNumber)) {
      return { value: asNumber, type: Number, format: NUMBER_FORMAT };
    }
  }

  if (column.type === 'date') {
    const asDate = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(asDate.getTime())) {
      return { value: asDate, type: Date, format: dateFormat };
    }
  }

  return { value: cellText(value), type: String };
}

export function toSheetData(
  rows: readonly any[],
  columns: ItpDataGridColumn[],
  dateFormat: string = FALLBACK_EXCEL_DATE_FORMAT,
  formatterFor?: ColumnFormatterLookup,
): XlsxCell[][] {
  const cols = exportableColumns(columns);
  const valueFor = columnValueLookup(cols);
  const header: XlsxCell[] = cols.map((column) => ({
    value: column.name || column.field,
    type: String,
    fontWeight: 'bold',
  }));
  const body = rows.map((row) =>
    cols.map((column) =>
      xlsxCell(row, column, valueFor(row, column), dateFormat, formatterFor),
    ),
  );
  return [header, ...body];
}

export function toSheetColumns(
  columns: ItpDataGridColumn[],
): { width?: number }[] {
  return exportableColumns(columns).map((column) => ({
    width: excelWidth(column.width),
  }));
}

export async function downloadXlsx(
  rows: readonly any[],
  columns: ItpDataGridColumn[],
  options: XlsxExportOptions = {},
): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const sheetData = toSheetData(
    rows,
    columns,
    options.dateFormat ?? FALLBACK_EXCEL_DATE_FORMAT,
    options.formatterFor,
  );
  const blob = await writeXlsxFile(sheetData as any, {
    columns: toSheetColumns(columns),
    sheet: options.sheet ?? gridMessages.export,
    stickyRowsCount: 1,
  }).toBlob();
  downloadBlob(blob, `${options.filename ?? 'export'}.xlsx`);
}
